import axios, { AxiosRequestHeaders } from 'axios';

import { District, DistrictId, DistrictListing } from './District';
import { FilterRangeConfiguration } from './FilterRangeConfiguration';
import { LegislativeDistrict } from './LegislativeDistrict';

export type intervalsParam = number;
export type minParam = number;
export type maxParam = number;

const currentYear = new Date().getFullYear();

export interface RequestParams {
  abortController?: AbortController;
}

export interface FetchFilterConfigurationsParams {
  acreage?: [intervalsParam, minParam, maxParam];
  yearCreated?: [intervalsParam, minParam, maxParam];
  bondsOutstanding?: [intervalsParam, minParam, maxParam];
  assessedValue?: [intervalsParam, minParam, maxParam];
  waterConnections?: [intervalsParam, minParam, maxParam];
  sanitaryConnections?: [intervalsParam, minParam, maxParam];
}

export interface FetchFilterConfigurationsResponse {
  districtType: string[];
  legislativeDistrict: LegislativeDistrict[];
  acreage: FilterRangeConfiguration;
  yearCreated: FilterRangeConfiguration;
  bondsOutstanding: FilterRangeConfiguration;
  assessedValue: FilterRangeConfiguration;
  waterConnections: FilterRangeConfiguration;
  sanitaryConnections: FilterRangeConfiguration;
}

export type QueryDistrictsParams = {
  /** xmin, ymin, xmax, ymax */
  mapExtent?: [number, number, number, number];
  districtName?: string;
  awbdMembership?: boolean;
  region?: string[];
  districtType?: string[];
  legislativeDistrict?: string[];
  acreage?: { min: number; max: number };
  yearCreated?: { min: number; max: number };
  bondsOutstanding?: { min: number; max: number };
  assessedValue?: { min: number; max: number };
  waterConnections?: { min: number; max: number };
  sanitaryConnections?: { min: number; max: number };
};

export type QueryDistrictsResponse = DistrictListing[];

export type FetchDistrictResponse = District;

export class APIService {
  private resourceEndpoints: Record<
    'filters' | 'districts' | 'district',
    string
  >;

  private requestHeaders: AxiosRequestHeaders;

  constructor(apiRootUrl: string, apiKey: string) {
    const url = apiRootUrl.replace(/\/$/, '');
    this.resourceEndpoints = {
      filters: [url, 'filters'].join('/'),
      districts: [url, 'districts'].join('/'),
      district: [url, 'districts'].join('/')
    };
    this.requestHeaders = { 'x-api-key': apiKey };
  }

  async fetchFilterConfigurations(
    {
      acreage = [20 + 2, 0, 2000 + 200],
      yearCreated = [
        50 + 1, // 1 interval per year
        currentYear - 50 + 1,
        currentYear + 1
      ],
      bondsOutstanding = [40 + 2, 0, 40000000 + 2000000],
      assessedValue = [40 + 2, 0, 400000000 + 20000000],
      waterConnections = [30 + 2, 0, 3000 + 200],
      sanitaryConnections = [30 + 2, 0, 3000 + 200]
    }: FetchFilterConfigurationsParams,
    { abortController }: RequestParams
  ): Promise<FetchFilterConfigurationsResponse> {
    const { data } = await axios.get<FetchFilterConfigurationsResponse>(
      this.resourceEndpoints.filters,
      {
        headers: this.requestHeaders,
        signal: abortController?.signal,
        params: {
          acreage: acreage.join(','),
          yearCreated: yearCreated.join(','),
          bondsOutstanding: bondsOutstanding.join(','),
          assessedValue: assessedValue.join(','),
          waterConnections: waterConnections.join(','),
          sanitaryConnections: sanitaryConnections.join(',')
        }
      }
    );

    return {
      ...data,
      acreage: { ...data.acreage, nodes: data.acreage.nodes.slice(0, 21) },
      yearCreated: {
        ...data.yearCreated,
        // yearCreated is a different because there typically is no value for the last interval
        // because this interval is the current year and there are delays in data updates.
        // This will create a max for the entire histogram of less than the last interval max,
        // causing issues in min/max calculations.
        // Problem resolved by removing last 2 intervals in ending chart like 2020+ if last year is 2021.
        nodes: data.yearCreated.nodes.slice(1, 49)
      },
      bondsOutstanding: {
        ...data.bondsOutstanding,
        nodes: data.bondsOutstanding.nodes.slice(0, 41)
      },
      assessedValue: {
        ...data.assessedValue,
        nodes: data.assessedValue.nodes.slice(0, 41)
      },
      waterConnections: {
        ...data.waterConnections,
        nodes: data.waterConnections.nodes.slice(0, 31)
      },
      sanitaryConnections: {
        ...data.sanitaryConnections,
        nodes: data.sanitaryConnections.nodes.slice(0, 31)
      }
    };
  }

  async queryDistricts(
    filterParams: QueryDistrictsParams,
    { abortController }: RequestParams
  ): Promise<QueryDistrictsResponse> {
    const mapMinMaxParam = ({ min, max }: { min: number; max: number }) =>
      [min, max].join(',');

    const params: Record<string, string | boolean> = {};

    if (filterParams.districtName)
      params.districtName = filterParams.districtName;
    if (filterParams.mapExtent)
      params.mapExtent = filterParams.mapExtent?.join(',');
    if (filterParams.awbdMembership !== undefined)
      params.isMember = filterParams.awbdMembership;
    if (filterParams.region?.length)
      params.region = filterParams.region.join(',');
    if (filterParams.districtType?.length)
      params.districtType = filterParams.districtType.join(',');
    if (filterParams.legislativeDistrict?.length)
      params.legislativeDistrict = filterParams.legislativeDistrict.join(',');
    if (filterParams.acreage)
      params.acreage = mapMinMaxParam(filterParams.acreage);
    if (filterParams.yearCreated)
      params.yearCreated = mapMinMaxParam(filterParams.yearCreated);
    if (filterParams.bondsOutstanding)
      params.bondsOutstanding = mapMinMaxParam(filterParams.bondsOutstanding);
    if (filterParams.assessedValue)
      params.assessedValue = mapMinMaxParam(filterParams.assessedValue);
    if (filterParams.waterConnections)
      params.waterConnections = mapMinMaxParam(filterParams.waterConnections);
    if (filterParams.sanitaryConnections)
      params.sanitaryConnections = mapMinMaxParam(
        filterParams.sanitaryConnections
      );

    const { data } = await axios.get<QueryDistrictsResponse>(
      this.resourceEndpoints.districts,
      {
        headers: this.requestHeaders,
        signal: abortController?.signal,
        params
      }
    );
    return data;
  }

  async fetchDistrict(
    districtId: DistrictId,
    { abortController }: RequestParams
  ): Promise<FetchDistrictResponse> {
    const { data } = await axios.get<{
      district: District;
      geoBounds: [number, number, number, number];
    }>([this.resourceEndpoints.district, districtId].join('/'), {
      headers: this.requestHeaders,
      signal: abortController?.signal
    });
    return { ...data.district, districtId, geoBounds: data.geoBounds };
  }
}
